import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
  Collapse,
  FormControl,
  Select,
  CircularProgress,
  FormHelperText,
} from "@mui/material";

import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";

import type { SelectChangeEvent } from "@mui/material/Select";

import { useNavigate, useLocation } from "react-router-dom";

import { authService, companyAdminService } from "../services";

import type { AdminCompany } from "../services";

import {
  companyConfigService,
  type CompanyPlan,
} from "../services/companyConfigService";

import { COMPANY_PLAN_OPTIONS } from "../constants/companyPlans";

import { alpha, useTheme } from "@mui/material/styles";

import GlobalStyles from "@mui/material/GlobalStyles";

import {
  BASE_SECTIONS,
  type MenuSectionConfig,
} from "../config/menuSections";

const drawerWidth = 240;

interface MainLayoutProps {
  children: React.ReactNode;
}

const PLAN_PRIORITY: CompanyPlan[] = [
  "VITRINE_APP",
  "GESTAO_PLUS",
  "WHITE_LABEL_BUSINESS",
  "FULL_CONTROL_ENTERPRISE",
];

interface CompanyUiMeta {
  plan: CompanyPlan;
  logoUrl?: string;
  razaoSocial?: string;
  slug?: string;
  message?: string;
}

const formatCnpj = (value?: string): string => {
  const digits = (value || "").replace(/\D/g, "").slice(0, 14);

  if (digits.length <= 2) return digits;

  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;

  if (digits.length <= 8)
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;

  if (digits.length <= 12) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  }

  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
};

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const navigate = useNavigate();

  const location = useLocation();

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  const theme = useTheme();

  const sectionSelectedBg = alpha(theme.palette.primary.main, 0.12);

  const sectionSelectedHover = alpha(theme.palette.primary.main, 0.18);

  const user = authService.getUser();

  const userRole = authService.getUserRole();

  const [companyMeta, setCompanyMeta] = useState<CompanyUiMeta>({
    plan: "VITRINE_APP",
  });

  const [developerCompanies, setDeveloperCompanies] = useState<AdminCompany[]>(
    [],
  );

  const [companiesLoading, setCompaniesLoading] = useState(false);

  const [companiesError, setCompaniesError] = useState<string | null>(null);

  const [selectedCompanyCnpj, setSelectedCompanyCnpj] = useState<
    string | "SELF" | ""
  >("SELF");

  const [isSwitchingCompany, setIsSwitchingCompany] = useState(false);

  const companyPlan = companyMeta.plan;

  const isDeveloper = userRole === "DEVELOPER";

  const hasPlanAccess = useCallback(
    (minPlan?: CompanyPlan) => {
      if (!minPlan) return true;

      if (isDeveloper) return true;

      if (!companyPlan) return false;

      const currentIndex = PLAN_PRIORITY.indexOf(companyPlan);

      const requiredIndex = PLAN_PRIORITY.indexOf(minPlan);

      if (currentIndex === -1 || requiredIndex === -1) {
        return false;
      }

      return currentIndex >= requiredIndex;
    },

    [companyPlan, isDeveloper],
  );

  useEffect(() => {
    let active = true;

    const loadConfig = async () => {
      try {
        const config = await companyConfigService.getMyConfig();

        if (!active || !config) {
          return;
        }

        setCompanyMeta({
          plan: (config.plan as CompanyPlan) ?? "VITRINE_APP",

          logoUrl: config.logoUrl || config.logoPath,

          razaoSocial: config.razaoSocial,

          slug: config.slug,

          message: config.message,
        });

        const impersonated = authService.getImpersonatedCompany();

        if (impersonated?.cnpj) {
          setSelectedCompanyCnpj(impersonated.cnpj);
        } else {
          setSelectedCompanyCnpj("SELF");
        }
      } catch (error) {
        console.error("[MainLayout] Failed to load company config", error);
      }
    };

    loadConfig();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isDeveloper) {
      setDeveloperCompanies([]);

      setCompaniesLoading(false);

      setCompaniesError(null);

      return;
    }

    let active = true;

    const loadCompanies = async () => {
      setCompaniesLoading(true);

      setCompaniesError(null);

      try {
        const companies = await companyAdminService.listCompanies();

        if (!active) {
          return;
        }

        setDeveloperCompanies(companies);
      } catch (error) {
        console.error(
          "[MainLayout] Failed to load companies for developer",
          error,
        );

        if (!active) {
          return;
        }

        setCompaniesError("Não foi possível carregar as empresas");
      } finally {
        if (active) {
          setCompaniesLoading(false);
        }
      }
    };

    loadCompanies();

    return () => {
      active = false;
    };
  }, [isDeveloper]);

  const sections = useMemo(() => {
    return BASE_SECTIONS.map((section) => {
      if (section.roles && (!userRole || !section.roles.includes(userRole))) {
        return null;
      }

      if (!hasPlanAccess(section.minPlan)) {
        return null;
      }

      const filteredItems = section.items.filter((item) => {
        if (item.roles && (!userRole || !item.roles.includes(userRole))) {
          return false;
        }

        if (!hasPlanAccess(item.minPlan)) {
          return false;
        }

        return true;
      });

      if (filteredItems.length === 0) {
        return null;
      }

      return {
        ...section,

        items: filteredItems,
      };
    })

      .filter((section): section is MenuSectionConfig => section !== null);
  }, [userRole, hasPlanAccess]);

  const flatMenuItems = useMemo(
    () => sections.flatMap((section) => section.items),

    [sections],
  );

  const activeMenuItem = useMemo(
    () =>
      flatMenuItems.find(
        (item) =>
          location.pathname === item.path ||
          location.pathname.startsWith(`${item.path}/`),
      ),

    [flatMenuItems, location.pathname],
  );

  const planDetails = useMemo(
    () =>
      COMPANY_PLAN_OPTIONS.find((option) => option.value === companyPlan) ??
      COMPANY_PLAN_OPTIONS[0],

    [companyPlan],
  );

  const handleCompanySelectChange = async (
    event: SelectChangeEvent<string>,
  ) => {
    const value = event.target.value as string;

    const previousValue = selectedCompanyCnpj;

    setSelectedCompanyCnpj(value);

    if (!isDeveloper) {
      return;
    }

    setIsSwitchingCompany(true);

    setCompaniesError(null);

    try {
      if (value === "SELF") {
        await authService.switchCompany({
          cnpj: null,
          companyName: companyMeta.razaoSocial,
        });
      } else {
        const selectedCompany = developerCompanies.find(
          (company) => company.cnpj === value,
        );

        await authService.switchCompany({
          cnpj: value,

          companyName: selectedCompany?.razaoSocial,
        });
      }

      const config = await companyConfigService.getMyConfig();

      if (config) {
        setCompanyMeta({
          plan: (config.plan as CompanyPlan) ?? "VITRINE_APP",

          logoUrl: config.logoUrl || config.logoPath,

          razaoSocial: config.razaoSocial,

          slug: config.slug,

          message: config.message,
        });

        if (value === "SELF") {
          setSelectedCompanyCnpj("SELF");
        } else {
          const updatedImpersonation = authService.getImpersonatedCompany();

          if (updatedImpersonation?.cnpj) {
            setSelectedCompanyCnpj(updatedImpersonation.cnpj);
          }
        }
      }
    } catch (error) {
      console.error("[MainLayout] Erro ao trocar de empresa", error);

      setSelectedCompanyCnpj(previousValue);

      setCompaniesError("Não foi possível trocar para a empresa selecionada");
    } finally {
      setIsSwitchingCompany(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    authService.logout();
  };

  useEffect(() => {
    setExpandedSections((prev) => {
      const activeSection = sections.find((section) =>
        section.items.some(
          (item) =>
            location.pathname === item.path ||
            location.pathname.startsWith(`${item.path}/`),
        ),
      );

      const next: Record<string, boolean> = {};

      sections.forEach((section) => {
        next[section.id] = false;
      });

      if (activeSection) {
        next[activeSection.id] = true;

        return next;
      }

      const previouslyOpenId = Object.keys(prev).find((key) => prev[key]);

      if (
        previouslyOpenId &&
        sections.some((section) => section.id === previouslyOpenId)
      ) {
        next[previouslyOpenId] = true;
      }

      return next;
    });
  }, [sections, location.pathname]);

  const toggleSection = (sectionId: string, defaultOpen?: boolean) => {
    setExpandedSections((prev) => {
      const isCurrentlyOpen = prev[sectionId] ?? defaultOpen ?? false;

      const next: Record<string, boolean> = {};

      sections.forEach((section) => {
        next[section.id] = false;
      });

      next[sectionId] = !isCurrentlyOpen;

      return next;
    });
  };

  const brandLogo =
    companyMeta.logoUrl && companyMeta.logoUrl.trim().length > 0
      ? companyMeta.logoUrl
      : undefined;

  const brandName = companyMeta.razaoSocial || "Better Admin";

  const brandInitial = useMemo(() => {
    const letter = companyMeta.razaoSocial?.trim()?.[0];

    if (letter) {
      return letter.toUpperCase();
    }

    return "B";
  }, [companyMeta.razaoSocial]);

  const drawer = (
    <div>
      <Toolbar sx={{ flexDirection: "column", alignItems: "center", py: 2 }}>
        {brandLogo ? (
          <Box
            component="img"
            src={brandLogo}
            alt={brandName}
            sx={{
              width: 72,

              height: 72,

              borderRadius: "30%",

              objectFit: "cover",

              mb: 1,

              boxShadow: 3,
            }}
          />
        ) : (
          <Avatar
            sx={{
              width: 72,

              height: 72,

              mb: 1,

              fontSize: "2rem",

              bgcolor: theme.palette.primary.main,

              color: theme.palette.getContrastText(theme.palette.primary.main),

              boxShadow: 3,

              borderRadius: "30%",
            }}
          >
            {brandInitial}
          </Avatar>
        )}

        <Typography variant="h6" component="div" align="center">
          {brandName}
        </Typography>

        <Box textAlign="center" sx={{ mt: 0.5 }}>
          {companyMeta.slug && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              /{companyMeta.slug}
            </Typography>
          )}

          {companyMeta.message && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 0.25 }}
              noWrap
            >
              {companyMeta.message}
            </Typography>
          )}
        </Box>
      </Toolbar>

      <Divider />

      <List sx={{ py: 0 }}>
        {sections.map((section) => {
          const SectionIcon = section.icon;

          const isExpanded = Object.prototype.hasOwnProperty.call(
            expandedSections,
            section.id,
          )
            ? expandedSections[section.id]
            : (section.defaultOpen ?? false);

          const sectionActive = section.items.some(
            (item) =>
              location.pathname === item.path ||
              location.pathname.startsWith(`${item.path}/`),
          );

          return (
            <React.Fragment key={section.id}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => toggleSection(section.id, section.defaultOpen)}
                  selected={sectionActive}
                >
                  <ListItemIcon>
                    <SectionIcon fontSize="small" />
                  </ListItemIcon>

                  <ListItemText primary={section.title} />

                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>

              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {section.items.map((item) => {
                    const ItemIcon = item.icon;

                    const isActive =
                      location.pathname === item.path ||
                      location.pathname.startsWith(`${item.path}/`);

                    return (
                      <ListItem key={item.path} disablePadding>
                        <ListItemButton
                          selected={isActive}
                          onClick={() => {
                            navigate(item.path);

                            setMobileOpen(false);
                          }}
                          sx={{
                            pl: 4,

                            "&.Mui-selected": {
                              backgroundColor: sectionSelectedBg,

                              color: theme.palette.primary.main,

                              "& .MuiListItemIcon-root, & svg": {
                                color: theme.palette.primary.main,
                              },
                            },

                            "&.Mui-selected:hover": {
                              backgroundColor: sectionSelectedHover,
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <ItemIcon fontSize="small" />
                          </ListItemIcon>

                          <ListItemText primary={item.text} />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </React.Fragment>
          );
        })}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <GlobalStyles
        styles={{
          "@media screen": {
            ".print-only": {
              display: "none !important",
            },
          },

          "@media print": {
            ".screen-only": {
              display: "none !important",
            },

            ".print-only": {
              display: "block !important",
            },

            ".MuiDrawer-root": {
              display: "none !important",
            },

            ".MuiAppBar-root": {
              display: "none !important",
            },

            ".app-main": {
              padding: "0 !important",

              width: "100% !important",

              backgroundColor: "#ffffff !important",
            },

            body: {
              backgroundColor: "#ffffff !important",
            },
          },
        }}
      />

      <AppBar
        className="screen-only"
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },

          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" }, color: "common.white" }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <span>{activeMenuItem?.text || "Better Admin"}</span>

            <Chip
              label={planDetails.label}
              size="small"
              color={
                companyPlan === "WHITE_LABEL_BUSINESS" ? "secondary" : "default"
              }
              sx={{
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.4,
              }}
            />
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {isDeveloper && (
              <FormControl
                size="small"
                sx={{ minWidth: 240, position: "relative" }}
                disabled={companiesLoading && developerCompanies.length === 0}
              >
                <Select
                  value={selectedCompanyCnpj}
                  onChange={handleCompanySelectChange}
                  displayEmpty
                  renderValue={(value) => {
                    if (value === "SELF") {
                      return companyMeta.razaoSocial ?? "Minha empresa";
                    }

                    const selected = developerCompanies.find(
                      (company) => company.cnpj === value,
                    );

                    return selected?.razaoSocial ?? `Empresa ${value}`;
                  }}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.08)",

                    color: "common.white",

                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255, 255, 255, 0.3)",
                    },

                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255, 255, 255, 0.6)",
                    },

                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "common.white",
                    },
                  }}
                  MenuProps={{
                    anchorOrigin: { vertical: "bottom", horizontal: "right" },

                    transformOrigin: { vertical: "top", horizontal: "right" },
                  }}
                >
                  <MenuItem value="SELF">
                    {companyMeta.razaoSocial
                      ? `Minha empresa (${companyMeta.razaoSocial})`
                      : "Minha empresa"}
                  </MenuItem>

                  {developerCompanies.length > 0 && (
                    <Divider sx={{ my: 0.5 }} />
                  )}

                  {companiesLoading && developerCompanies.length === 0 && (
                    <MenuItem value="" disabled>
                      Carregando empresas...
                    </MenuItem>
                  )}

                  {developerCompanies.map((company) => (
                    <MenuItem key={company.id} value={company.cnpj}>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {company.razaoSocial ?? "Sem razão social"}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                          {formatCnpj(company.cnpj)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>

                {(companiesLoading || isSwitchingCompany) && (
                  <CircularProgress
                    size={16}
                    color="inherit"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      right: 12,
                      mt: "-8px",
                    }}
                  />
                )}

                {companiesError && (
                  <FormHelperText sx={{ color: "error.main" }}>
                    {companiesError}
                  </FormHelperText>
                )}
              </FormControl>
            )}

            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.name?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>

            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",

                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",

                horizontal: "right",
              }}
              open={Boolean(anchorEl)}
              onClose={handleProfileMenuClose}
            >
              <MenuItem disabled>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">{user?.name}</Typography>

                  {userRole === "DEVELOPER" && (
                    <Chip
                      label="DEV"
                      size="small"
                      color="secondary"
                      sx={{ fontSize: "0.6rem", height: "16px" }}
                    />
                  )}
                </Box>

                <Typography
                  variant="caption"
                  display="block"
                  color="textSecondary"
                >
                  {user?.email}
                </Typography>
              </MenuItem>

              <Divider />

              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>

                <ListItemText>Sair</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
        className="screen-only"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: "none" },

            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          className="screen-only"
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },

            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,

          p: { xs: 2, sm: 3 },

          width: { xs: "100%", sm: `calc(100% - ${drawerWidth}px)` },
        }}
        className="app-main"
      >
        <Toolbar className="screen-only" />

        {children}
      </Box>
    </Box>
  );
};

export default MainLayout;
